import { ConfirmDialog } from "@/components/shared/confirm-dialog";
import { canViewAll } from "@/lib/permissions";
import { APP_ROLE_LABELS, type AppRole } from "@/schemas/users";

/**
 * Whether viewing as this account needs confirming first.
 *
 * Gated on `canViewAll` rather than a literal `"admin"`, so `admin_readonly`
 * gets the same warning: it too opens every profile and every admin-only
 * report, which is the thing worth pausing over. A plain user or a manager
 * grants the viewer nothing they did not already have, so neither prompts.
 */
export function viewAsNeedsConfirming(role: string | null | undefined): boolean {
  return canViewAll(role);
}

function roleLabel(role: string | null | undefined): string {
  if (role !== null && role !== undefined && role in APP_ROLE_LABELS) {
    return APP_ROLE_LABELS[role as AppRole];
  }
  // A role a newer migration introduced, or a legacy value. Naming it rather
  // than guessing a label keeps the sentence true.
  return role?.trim() ? role : "an elevated";
}

/**
 * Confirm before viewing as someone who holds administrator access (WI, HR
 * feedback 2026-09-22).
 *
 * Until now the whole question was moot — Better Auth refused an admin target
 * outright. Now that it is allowed, the viewer genuinely holds that person's
 * access for the duration, which is a materially different act from looking at
 * the app as an employee. One dialog, shared by both entry points (the account
 * menu picker and the button on a person's profile), so the wording cannot
 * drift between them.
 */
export function ConfirmViewAsElevatedDialog({
  open,
  onOpenChange,
  name,
  role,
  onConfirm,
}: {
  open: boolean;
  onOpenChange: (open: boolean) => void;
  /** Who you are about to view as. */
  name: string;
  /** Their `user.role`, used to name the access level in the sentence. */
  role: string | null | undefined;
  onConfirm: () => void;
}) {
  return (
    <ConfirmDialog
      open={open}
      onOpenChange={onOpenChange}
      title={`View as ${name}?`}
      description={`${name} has ${roleLabel(role)} access. While you are viewing as them you hold that access yourself — every administrator page and every person's record is open to you. Anything you do is recorded in the audit log under your own name, not theirs.`}
      confirmLabel={`View as ${name}`}
      onConfirm={onConfirm}
    />
  );
}
